import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, FileText, Send, DollarSign, Calendar, Users, Zap, Clock, Shield, TrendingUp, 
  Mail, CreditCard, Lock, Image, Building, MessageSquare, Bell, Repeat, Wrench, Receipt, 
  Briefcase, Eye, Smartphone, Palette, Globe, TicketCheck, ArrowRight, Star, Sparkles,
  Check, XCircle, Bot, Wand2, BarChart3, FileStack, UserPlus, PenTool, Handshake, Percent, Cookie, Wallet
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import logo from "@/assets/logo.png";

import { EvergreenPromise } from "@/components/EvergreenPromise";
import { BrandlessVideoPlayer } from "@/components/BrandlessVideoPlayer";
import salesHeroWorkflow from "@/assets/sales-hero-workflow.jpg";
import salesProblemSolution from "@/assets/sales-problem-solution.jpg";
import salesIndustryPros from "@/assets/sales-industry-pros.jpg";
import salesTestimonials from "@/assets/sales-testimonials.jpg";
import salesComparisonWinner from "@/assets/sales-comparison-winner.jpg";
import salesSecurityTrust from "@/assets/sales-security-trust.jpg";
import salesFaq from "@/assets/sales-faq.jpg";
import FounderStory from "@/components/FounderStory";

const scrollToPricing = () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });

const IntervalCTA = () => (
  <section className="py-12 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border-y border-primary/10">
    <div className="container mx-auto px-4">
      <div className="max-w-xl mx-auto">
        <Card className="border-2 border-primary/30 bg-card shadow-xl overflow-hidden">
          <CardContent className="pt-8 pb-8 space-y-5 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">Get QuoteSendGo Pro — Starting at $29/Month</h3>
            <p className="text-muted-foreground">
              AI-powered quoting, invoicing & payments — <strong className="text-primary">all in one plan</strong>
            </p>
            <Button
              size="lg"
              className="w-full max-w-md text-lg py-6 font-bold shadow-lg hover:scale-105 transition-all"
              onClick={scrollToPricing}
            >
              👉 See Pricing & Get Started
            </Button>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-success" /> 14-Day Free Trial</span>
              <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" /> No Credit Card Required</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

const Index = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(true);

  const categoryScreenshots: Record<string, string> = {
    "Scheduling & Appointments": "/images/screenshots/appointments.png",
    "Invoicing & Payments": "/images/screenshots/invoices.png",
    "Client Management": "/images/screenshots/clients.png",
    "Professional Quoting": "/images/screenshots/quotes.png",
    "Notifications & Reminders": "/images/screenshots/notifications.png",
    "Projects & Workflow": "/images/screenshots/projects.png",
    "Business Tools": "/images/screenshots/services.png",
  };

  const featureCategories = [
    {
      title: "AI & Power Tools",
      description: "Let AI do the heavy lifting — build quotes, write content, and more",
      icon: Sparkles,
      color: "from-purple-600 to-pink-500",
      features: [
        { icon: Wand2, title: "AI Quote Builder", description: "Describe your project in plain English — AI generates a complete, itemized quote in seconds. No templates needed." },
        { icon: PenTool, title: "AI Line Descriptions", description: "Auto-generate professional descriptions for every line item. Sound polished without writing a word." },
        { icon: Globe, title: "AI Website Copy", description: "Generate your entire business website content with one click. Headlines, about text, services — all AI-written." },
        { icon: FileStack, title: "Template Vault", description: "Save your best quotes as reusable templates. Load them in one click and customize for each client." },
        { icon: BarChart3, title: "Advanced Reporting", description: "Revenue trends, quote conversion rates, payment timelines — get insights that help you grow." },
        { icon: Palette, title: "Pro Branding Suite", description: "Full custom colors, white-label branding, and personalized client-facing pages. Your brand, everywhere." },
      ]
    },
    {
      title: "Scheduling & Appointments",
      description: "Let clients book appointments online 24/7",
      icon: Calendar,
      color: "from-orange-500 to-amber-500",
      features: [
        { icon: Globe, title: "Public Booking Page", description: "Share your custom booking link and let clients schedule appointments online. Available 24/7." },
        { icon: Calendar, title: "Working Hours", description: "Set your availability for each day of the week. Block out dates for vacations or holidays." },
        { icon: Clock, title: "Appointment Slots", description: "Configure appointment duration, buffer time, and lead time requirements to fit your workflow." },
        { icon: Wrench, title: "Service Types", description: "Define service types clients can choose from when booking. Categorize estimates vs. work appointments." },
        { icon: Users, title: "Auto Client Creation", description: "New clients are automatically added to your database when they book. No manual data entry needed." },
        { icon: FileText, title: "Appointment-to-Quote", description: "Create quotes directly from appointments with client info pre-filled. Streamlined workflow." },
        { icon: Globe, title: "Embeddable Booking Widget", description: "Embed your booking calendar directly on your own website. Clients book without leaving your site." }
      ]
    },
    {
      title: "Invoicing & Payments",
      description: "Get paid faster with integrated invoicing",
      icon: Receipt,
      color: "from-emerald-500 to-teal-500",
      features: [
        { icon: Receipt, title: "Professional Invoices", description: "Create and send invoices for completed work. Convert quotes to invoices seamlessly with one click." },
        { icon: CreditCard, title: "PayPal Integration", description: "Accept credit cards, debit cards, and PayPal payments. Funds go directly to your PayPal account." },
        { icon: DollarSign, title: "Deposit Collection", description: "Collect deposits on approved quotes to secure projects. Reduce no-shows and last-minute cancellations." },
        { icon: Eye, title: "Invoice Tracking", description: "Track when invoices are viewed and paid. Get instant notifications when payments arrive." },
        { icon: Repeat, title: "Recurring Billing", description: "Set up recurring invoices for ongoing services. Auto-generate and send invoices on schedule." },
        { icon: FileStack, title: "Payment Plans", description: "Split large invoices into manageable installments. Clients pay on a schedule that works for everyone." },
        { icon: Shield, title: "Secure Payments", description: "Bank-level security through PayPal. We never store sensitive payment data—your clients are protected." },
        { icon: Receipt, title: "Record Offline Payments", description: "Record cash, check, or bank transfer payments manually. Keep your books accurate for all payment types." }
      ]
    },
    {
      title: "Client Management",
      description: "Keep all client info organized in one place",
      icon: Users,
      color: "from-violet-500 to-purple-500",
      features: [
        { icon: Users, title: "Client Database", description: "Store client contact info, billing addresses, and complete history. Never lose track of a customer." },
        { icon: Building, title: "Multiple Properties", description: "Manage clients with multiple service locations. Select the right property when creating quotes." },
        { icon: MessageSquare, title: "Messaging Portal", description: "Built-in messaging system for client communication. Keep all conversations organized by client." },
        { icon: Clock, title: "Complete History", description: "View every quote, project, invoice, and payment for each client. Full relationship timeline at a glance." },
        { icon: FileText, title: "Quick Quote Creation", description: "Create new quotes directly from client profiles with info pre-filled. Save time on every job." },
        { icon: Globe, title: "Client Portal", description: "Clients can access their quotes, invoices, and messages through a secure portal link." }
      ]
    },
    {
      title: "Professional Quoting",
      description: "Create stunning quotes that win more jobs",
      icon: FileText,
      color: "from-blue-500 to-cyan-500",
      features: [
        { icon: FileText, title: "Beautiful Quotes", description: "Create professional, branded quotes with custom titles, descriptions, and detailed line items that impress clients." },
        { icon: Image, title: "Line Item Images", description: "Add up to 5 photos per line item to show your work, materials, or project scope. Visual quotes close deals faster." },
        { icon: CheckCircle, title: "Optional Items", description: "Let clients customize their quote by toggling optional add-ons and upgrades. Perfect for upselling services." },
        { icon: DollarSign, title: "Flexible Deposits", description: "Set deposits as a percentage or fixed amount. Collect upfront payments to secure the job before work begins." },
        { icon: Eye, title: "View Tracking", description: "Know exactly when clients open your quotes. Get notifications and see view timestamps for smart follow-ups." },
        { icon: Send, title: "One-Click Sending", description: "Send quotes instantly via email with your branding. Clients receive a secure link to view and approve." }
      ]
    },
    {
      title: "Notifications & Reminders",
      description: "Never miss an important update or follow-up",
      icon: Bell,
      color: "from-pink-500 to-rose-500",
      features: [
        { icon: Mail, title: "Email Notifications", description: "Automatic emails to clients for quotes, invoices, and appointment confirmations." },
        { icon: Smartphone, title: "SMS Notifications", description: "Get instant text alerts for quote views, approvals, payments, bookings, and client messages." },
        { icon: Repeat, title: "Automated Reminders", description: "Set up automatic follow-up emails for pending quotes and unpaid invoices. Never forget a follow-up." },
        { icon: Eye, title: "View Alerts", description: "Get notified the moment a client opens your quote or invoice. Perfect timing for follow-up calls." },
        { icon: DollarSign, title: "Payment Alerts", description: "Instant notifications when deposits or invoice payments are received. Know the second you get paid." },
        { icon: Calendar, title: "Booking Alerts", description: "Get notified for new appointment bookings and cancellations. Stay on top of your schedule." },
        { icon: Mail, title: "Custom Email Templates", description: "Customize the emails sent for quotes, invoices, reminders, and more. Match your brand voice in every message." }
      ]
    },
    {
      title: "Projects & Workflow",
      description: "Track jobs from quote to completion",
      icon: Briefcase,
      color: "from-indigo-500 to-blue-500",
      features: [
        { icon: Briefcase, title: "Automatic Projects", description: "Projects are created automatically when quotes are approved. Track work from start to finish." },
        { icon: Clock, title: "Project Status", description: "Mark projects as open, in progress, or completed. Clear visibility into your workload." },
        { icon: Receipt, title: "Invoice from Projects", description: "Create invoices directly from completed projects with all details pre-filled." },
        { icon: FileText, title: "Internal Notes", description: "Add private notes to projects for your reference. Track important details and site info." },
        { icon: Building, title: "Site Address", description: "Project addresses are carried over from quotes. Always know where the job is located." },
        { icon: Users, title: "Client Connection", description: "Projects stay linked to clients and quotes for complete relationship history." }
      ]
    },
    {
      title: "Team & Collaboration",
      description: "Invite your team and work together seamlessly",
      icon: UserPlus,
      color: "from-teal-500 to-cyan-500",
      features: [
        { icon: UserPlus, title: "Team Members", description: "Invite staff to your account with role-based access. Collaborate on quotes, projects, and client management." },
        { icon: Shield, title: "Permission Controls", description: "Control what each team member can see and do. Protect sensitive financial data and client info." },
        { icon: Mail, title: "Team Notifications", description: "Keep your team informed with shared notifications for bookings, payments, and client messages." },
      ]
    },
    {
      title: "Business Tools",
      description: "Everything else you need to run your business",
      icon: Wrench,
      color: "from-slate-500 to-gray-500",
      features: [
        { icon: Wrench, title: "Service Catalog", description: "Create reusable service templates with preset pricing. Add to quotes and invoices in seconds." },
        { icon: Globe, title: "Business Website", description: "Get a professional public website for your business — showcases your services, contact info, and online booking. No coding needed." },
        { icon: Palette, title: "Custom Branding", description: "Upload your logo and set brand colors. All client-facing documents use your branding." },
        { icon: TicketCheck, title: "In-App Support", description: "Submit and track support tickets directly from your dashboard. Get help when you need it." },
        { icon: Smartphone, title: "Installable Mobile App", description: "Install QuoteSendGo on your phone's home screen — works like a native app on iPhone, Android, and desktop. No app store needed." },
        { icon: Users, title: "Easy Sign-In", description: "Sign in quickly with Google or email/password. No complicated setup required." },
        { icon: Eye, title: "Dashboard Overview", description: "See pending quotes, recent activity, and quick actions all in one place." },
        { icon: Shield, title: "Secure & Reliable", description: "Your data is encrypted and backed up. Access your business info anytime, anywhere." },
        { icon: Receipt, title: "Expense Tracking", description: "Track business expenses by category, client, or project. Attach receipts and keep everything organized for tax time." },
        { icon: Star, title: "Google Review Requests", description: "Automatically request Google reviews from happy clients after completed projects. Build your online reputation effortlessly." },
        { icon: Globe, title: "Custom Domains", description: "Connect your own domain to your QuoteSendGo website and booking page. Look fully professional with your own web address." }
      ]
    }
  ];

  const testimonials = [
    { quote: "I used to spend hours on quotes in Word. Now I can create a professional quote in 5 minutes and send it before I leave the job site.", author: "Mike D.", role: "General Contractor", rating: 5 },
    { quote: "The view tracking is a game-changer. I know exactly when clients open my quotes and can follow up at the perfect time.", author: "Sarah T.", role: "Landscaping Business Owner", rating: 5 },
    { quote: "Getting paid used to take weeks. Now clients pay deposits online before I even start work. Cash flow has never been better.", author: "James R.", role: "Handyman Services", rating: 5 }
  ];

  const stats = [
    { value: "24/7", label: "Client Booking", description: "Accept jobs anytime" },
    { value: "75%", label: "Faster Quote Creation", description: "Compared to Word/Excel" },
    { value: "2x", label: "Faster Payments", description: "With online deposits" },
    { value: "100%", label: "Organized", description: "All info in one place" },
  ];

  const problemSolutions = [
    { problem: "Missing calls and appointment requests", solution: "24/7 online booking page for clients to self-schedule" },
    { problem: "Hours creating quotes in Word or Excel", solution: "AI builds a complete quote from a simple description" },
    { problem: "Losing track of sent quotes and follow-ups", solution: "Automatic tracking and reminder emails" },
    { problem: "Waiting weeks to get paid after completing work", solution: "Online deposits and instant payments" },
    { problem: "Managing quotes, invoices, and clients in different places", solution: "Everything organized in one dashboard" },
    { problem: "No visibility into quote status", solution: "Real-time view tracking and SMS notifications" }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <img src={logo} alt="QuoteSendGo Logo" className="h-10 w-auto" width="500" height="120" loading="eager" decoding="async" />
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => navigate("/auth")}>Sign In</Button>
            <Button onClick={scrollToPricing}>
              <Sparkles className="w-4 h-4 mr-2" />
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,hsl(var(--primary)/0.15),transparent)]" />
          
          <div className="container relative mx-auto px-4 py-16 md:py-24 lg:py-32">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              <Badge variant="secondary" className="px-4 py-2 text-sm font-medium">
                <Sparkles className="w-4 h-4 mr-2" />
                AI-Powered Scheduling, Quoting, Invoicing & Payments
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight tracking-tight">
                Book Jobs, Send Quotes, <span className="text-primary">Get Paid Faster</span>
              </h1>
              
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Describe your project in plain English — <strong className="text-primary">AI builds the quote for you in seconds.</strong> Then send invoices, collect payments, and manage your entire business in one place.
              </p>
              
              {/* Sales Video */}
              <div className="relative w-full max-w-3xl mx-auto rounded-xl overflow-hidden shadow-2xl border border-border/50">
                <BrandlessVideoPlayer
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/marketing-videos/index-sales.mp4`}
                  title="QuoteSendGo Sales Video"
                />
              </div>
              
              <Card className="border-2 border-primary/30 bg-card shadow-xl overflow-hidden max-w-xl mx-auto mt-6">
                <CardContent className="pt-8 pb-8 space-y-5 text-center">
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground">Get QuoteSendGo Pro — Starting at $29/Month</h3>
                  <p className="text-muted-foreground">
                    One plan. All features. AI included. <strong className="text-primary">No hidden fees.</strong>
                  </p>
                  <Button
                    size="lg"
                    className="w-full max-w-md text-lg py-6 font-bold shadow-lg hover:scale-105 transition-all"
                    onClick={scrollToPricing}
                  >
                    👉 See Pricing & Get Started
                  </Button>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-success" /> 14-Day Free Trial</span>
                    <span className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-primary" /> No Credit Card Required</span>
                    <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" /> Instant Access</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* AI-Powered Section */}
        <section className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-br from-purple-500/10 via-primary/5 to-pink-500/10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(var(--primary)/0.08),transparent)]" />
          <div className="container relative mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12 space-y-4">
                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10 px-4 py-2">
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI-POWERED TOOLS
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">Let AI Do the Heavy Lifting</h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">Stop spending hours on quotes and content. Describe what you need — AI handles the rest.</p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Wand2, title: "AI Quote Builder", desc: "Describe your project in plain English and get a complete, itemized quote with line items and pricing — in seconds.", color: "from-purple-500 to-violet-600" },
                  { icon: PenTool, title: "AI Line Descriptions", desc: "Auto-generate professional, detailed descriptions for every line item. Sound polished without writing a word.", color: "from-blue-500 to-indigo-600" },
                  { icon: Globe, title: "AI Website Copy", desc: "Generate your entire business website — headlines, about text, services, SEO metadata — with one click.", color: "from-pink-500 to-rose-600" },
                  { icon: Bot, title: "AI Chatbot Widget", desc: "Add an AI assistant to your website that answers client questions, collects leads, and works 24/7.", color: "from-teal-500 to-emerald-600" },
                ].map((item, i) => (
                  <Card key={i} className="border-border/50 bg-card/80 backdrop-blur hover:shadow-xl transition-all duration-300 group overflow-hidden">
                    <CardHeader className="space-y-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                        <item.icon className="w-7 h-7 text-white" />
                      </div>
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center mt-10">
                <Button size="lg" className="text-lg py-6 px-8 font-bold shadow-lg hover:scale-105 transition-all" onClick={scrollToPricing}>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Get AI-Powered Quoting — Starting at $29/mo
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section with Workflow Graphic */}
        <section className="py-12 bg-muted/30 border-y border-border/50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10">
              <div className="w-full md:w-1/2">
                <img src={salesHeroWorkflow} alt="Service professional managing business on tablet" className="rounded-2xl shadow-xl w-full" loading="lazy" />
              </div>
              <div className="w-full md:w-1/2">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat) => (
                    <div key={stat.label} className="text-center p-4 bg-card rounded-xl border border-border/50 shadow-sm">
                      <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{stat.value}</div>
                      <div className="font-semibold text-foreground">{stat.label}</div>
                      <div className="text-sm text-muted-foreground">{stat.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem → Solution Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Sound Familiar?</h2>
                <p className="text-xl text-muted-foreground">We built Quote Send Go to solve these exact problems</p>
              </div>
              
              <div className="mb-10">
                <img src={salesProblemSolution} alt="From chaos to organized business management" className="rounded-2xl shadow-xl w-full" loading="lazy" />
              </div>
              
              <div className="space-y-4">
                {problemSolutions.map((item, index) => (
                  <Card key={index} className="border-border/50 overflow-hidden">
                    <CardContent className="p-0">
                      <div className="grid md:grid-cols-2">
                        <div className="p-6 bg-destructive/5 border-r border-border/50">
                          <div className="flex items-start gap-3">
                            <span className="text-destructive text-xl mt-0.5">✗</span>
                            <p className="text-muted-foreground">{item.problem}</p>
                          </div>
                        </div>
                        <div className="p-6 bg-primary/5">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <p className="text-foreground font-medium">{item.solution}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Tools QuoteSendGo Replaces */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12 space-y-4">
                <Badge variant="secondary" className="px-4 py-2 text-sm font-medium">
                  <DollarSign className="w-4 h-4 mr-2" />
                  STOP PAYING FOR 6+ TOOLS
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">One Platform Replaces Everything</h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">Most service businesses juggle 6+ separate tools and subscriptions. QuoteSendGo replaces them all — for a fraction of the cost.</p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                {[
                  { tool: "Calendly / Acuity", purpose: "Online Scheduling", cost: "$16/mo", icon: Calendar },
                  { tool: "PandaDoc / Proposify", purpose: "Quotes & Proposals", cost: "$35/mo", icon: FileText },
                  { tool: "QuickBooks / FreshBooks", purpose: "Invoicing & Billing", cost: "$30/mo", icon: Receipt },
                  { tool: "Mailchimp / SendGrid", purpose: "Email Notifications", cost: "$20/mo", icon: Mail },
                  { tool: "Wix / Squarespace", purpose: "Business Website", cost: "$16/mo", icon: Globe },
                  { tool: "HubSpot / Salesforce", purpose: "Client CRM", cost: "$25/mo", icon: Users },
                  { tool: "Jasper / Copy.ai", purpose: "AI Content Writing", cost: "$39/mo", icon: Wand2 },
                  { tool: "Twilio / SimpleTexting", purpose: "SMS Notifications", cost: "$20/mo", icon: Smartphone },
                  { tool: "Intercom / Drift", purpose: "Chatbot Widget", cost: "$29/mo", icon: Bot },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border/50">
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground text-sm">{item.tool}</div>
                      <div className="text-xs text-muted-foreground">{item.purpose}</div>
                    </div>
                    <div className="text-sm font-bold text-destructive line-through whitespace-nowrap">{item.cost}</div>
                  </div>
                ))}
              </div>

              <div className="max-w-lg mx-auto">
                <Card className="border-2 border-primary bg-primary/5 shadow-xl">
                  <CardContent className="pt-8 pb-8 text-center space-y-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground uppercase tracking-wide font-medium">Total cost of separate tools</p>
                      <p className="text-4xl md:text-5xl font-black text-destructive line-through">$230+/mo</p>
                    </div>
                    <div className="flex items-center justify-center gap-3 py-2">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-sm font-semibold text-muted-foreground">vs.</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground uppercase tracking-wide font-medium">QuoteSendGo — everything included</p>
                      <p className="text-5xl md:text-6xl font-black text-primary">$39/mo</p>
                    </div>
                    <p className="text-lg font-semibold text-success">You save over $190/month ($2,280+/year)</p>
                    <Button
                      size="lg"
                      className="w-full max-w-sm text-lg py-6 font-bold shadow-lg hover:scale-105 transition-all"
                      onClick={scrollToPricing}
                    >
                      👉 Replace All Your Tools — $39/mo
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <IntervalCTA />

        {/* How It Works — 4-Step Workflow */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 max-w-5xl space-y-12">
            <div className="text-center space-y-3">
              <h2 className="text-3xl md:text-4xl font-bold">From Booking to Payment in 4 Simple Steps</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">No learning curve. No complicated setup. Just a simple workflow that takes you from first contact to getting paid.</p>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: "1", icon: Calendar, title: "Client Books", desc: "Clients book appointments through your online calendar — embedded on your website or shared via link. You get notified instantly.", color: "from-blue-500 to-cyan-500" },
                { step: "2", icon: Wand2, title: "AI Builds Quote", desc: "Describe the project in plain English — AI generates a professional, itemized quote in seconds. Review, customize, and send with one click.", color: "from-purple-500 to-pink-500" },
                { step: "3", icon: Briefcase, title: "Track Project", desc: "Approved quotes convert to projects automatically. Track progress, manage timelines, and keep everything organized in one place.", color: "from-amber-500 to-orange-500" },
                { step: "4", icon: DollarSign, title: "Get Paid", desc: "Generate invoices from completed projects. Clients pay online via PayPal — you get notified the second payment lands.", color: "from-emerald-500 to-teal-500" },
              ].map(({ step, icon: Icon, title, desc, color }) => (
                <div key={step} className="relative bg-card border border-border/50 rounded-2xl p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                    {step}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      <h3 className="font-bold text-lg">{title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                  {step !== "4" && (
                    <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                      <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Categories with Screenshots */}
        {featureCategories.map((category, categoryIndex) => (
          <>
            {categoryIndex === 4 && <IntervalCTA />}
            <section 
              key={categoryIndex}
              className={`py-20 ${categoryIndex % 2 === 0 ? 'bg-muted/30' : 'bg-background'}`}
            >
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="text-center mb-12">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${category.color} mb-6`}>
                      <category.icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{category.title}</h2>
                    <p className="text-xl text-muted-foreground">{category.description}</p>
                  </div>

                  {/* Screenshot Showcase */}
                  {categoryScreenshots[category.title] && (
                    <div className="mb-12 max-w-4xl mx-auto">
                      <div className="rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-card">
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/80 border-b border-border/50">
                          <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-400" />
                            <div className="w-3 h-3 rounded-full bg-yellow-400" />
                            <div className="w-3 h-3 rounded-full bg-green-400" />
                          </div>
                          <div className="flex-1 mx-8">
                            <div className="bg-background rounded-md px-3 py-1 text-xs text-muted-foreground text-center border border-border/50">
                              app.quotesendgo.com
                            </div>
                          </div>
                        </div>
                        <img 
                          src={categoryScreenshots[category.title]} 
                          alt={`${category.title} screenshot`}
                          className="w-full h-auto"
                          loading="lazy"
                          onError={(e) => {
                            const showcase = (e.target as HTMLElement).closest('.max-w-4xl');
                            if (showcase) (showcase as HTMLElement).style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.features.map((feature, featureIndex) => (
                      <Card key={featureIndex} className="border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group">
                        <CardHeader className="flex flex-row items-start gap-4 space-y-0">
                          <div className={`w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                            <feature.icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{feature.title}</CardTitle>
                            <CardDescription className="text-sm leading-relaxed mt-1">{feature.description}</CardDescription>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </>
        ))}

        <IntervalCTA />

        {/* Overview Video */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">See QuoteSendGo In Action</h2>
              <p className="text-lg text-muted-foreground">Watch how it streamlines your entire quote-to-payment workflow</p>
            </div>
            <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-xl">
              <BrandlessVideoPlayer
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/marketing-videos/index-overview.mp4`}
                title="QuoteSendGo Overview Video"
              />
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-primary text-primary-foreground relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,hsl(var(--primary-foreground)/0.1),transparent)]" />
          
          <div className="container relative mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Trusted by Service Professionals</h2>
              <p className="text-xl text-primary-foreground/80">Join contractors who've transformed their quote-to-payment workflow</p>
            </div>

            <div className="mb-12 max-w-2xl mx-auto">
              <img src={salesTestimonials} alt="5-star reviews from service professionals" className="rounded-2xl shadow-xl w-full opacity-90" loading="lazy" />
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="bg-primary-foreground/10 border-primary-foreground/20 backdrop-blur">
                  <CardContent className="pt-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <blockquote className="text-primary-foreground mb-6 leading-relaxed">"{testimonial.quote}"</blockquote>
                    <div>
                      <div className="font-semibold text-primary-foreground">{testimonial.author}</div>
                      <div className="text-sm text-primary-foreground/70">{testimonial.role}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Who Is This For */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="mb-12">
                <img src={salesIndustryPros} alt="Service professionals who use QuoteSendGo" className="rounded-2xl shadow-xl w-full object-cover max-h-80" loading="lazy" />
              </div>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for Service Businesses</h2>
                <p className="text-xl text-muted-foreground">Quote Send Go is perfect for professionals who need to send quotes, manage projects, and get paid quickly</p>
              </div>

              <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { title: "Contractors & Construction", desc: "General contractors, remodelers, and construction companies managing multiple projects." },
                  { title: "Landscaping & Lawn Care", desc: "Landscapers, lawn care services, and outdoor maintenance businesses." },
                  { title: "Cleaning Services", desc: "Residential and commercial cleaning companies managing recurring clients." },
                  { title: "HVAC & Plumbing", desc: "HVAC technicians, plumbers, and electricians quoting jobs on-site." },
                  { title: "Handyman & Repairs", desc: "Handyman services, repair specialists, and maintenance professionals." },
                  { title: "Consultants & Professionals", desc: "Consultants, coaches, and project-based service providers." }
                ].map((industry, index) => (
                  <Card key={index} className="border-border/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        {industry.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{industry.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Founder Story */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="text-sm px-4 py-1 mb-4">MEET THE FOUNDER</Badge>
              <h2 className="text-4xl font-black text-foreground">Built by a Contractor, for Contractors & Service Professionals</h2>
            </div>
            <FounderStory compact />
          </div>
        </section>

        <IntervalCTA />

        {/* Comparison Table */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="px-4 py-2 text-sm font-medium mb-6">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  How We Compare
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Quote Send Go vs. The Competition</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">All the features you need at a fraction of the price. No bloated software, no complicated setup, no per-user fees.</p>
                <div className="mt-8 max-w-md mx-auto">
                  <img src={salesComparisonWinner} alt="QuoteSendGo wins the comparison" className="rounded-2xl shadow-lg w-full" loading="lazy" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-4 bg-muted/50 rounded-tl-xl border-b border-border/50 font-semibold text-foreground">Features</th>
                      <th className="p-4 bg-primary/10 border-b border-border/50">
                        <div className="flex flex-col items-center gap-2">
                          <img src={logo} alt="Quote Send Go" className="h-8 w-auto" width="500" height="120" />
                          <span className="font-bold text-primary">Quote Send Go</span>
                          <span className="text-sm text-primary font-semibold">$39/mo</span>
                        </div>
                      </th>
                      <th className="p-4 bg-muted/50 border-b border-border/50">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-2xl font-bold text-muted-foreground">Jobber</span>
                          <span className="text-sm text-muted-foreground">From $69/mo</span>
                        </div>
                      </th>
                      <th className="p-4 bg-muted/50 rounded-tr-xl border-b border-border/50">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-2xl font-bold text-muted-foreground">ServiceTitan</span>
                          <span className="text-sm text-muted-foreground">From $300+/mo</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { 
                        category: "Pricing & Value",
                        features: [
                          { name: "Monthly Price", qsg: "$39/mo flat", jobber: "$69-$259/mo", titan: "$300+/mo" },
                          { name: "Per-User Fees", qsg: true, qsgNote: "None", jobber: false, jobberNote: "$29+/user", titan: false, titanNote: "Per user" },
                          { name: "Platform Transaction Fees", qsg: true, qsgNote: "0%", jobber: false, jobberNote: "0.5-1.5%", titan: false, titanNote: "Varies" },
                          { name: "Free Business Website", qsg: true, jobber: false, titan: false },
                          { name: "Free Mobile App (PWA)", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "Quoting & Invoicing",
                        features: [
                          { name: "Professional Quotes", qsg: true, jobber: true, titan: true },
                          { name: "Online Invoice Payments", qsg: true, jobber: true, titan: true },
                          { name: "Deposit Collection", qsg: true, jobber: true, titan: true },
                          { name: "Line Item Images", qsg: true, jobber: false, titan: false },
                          { name: "Optional Line Items", qsg: true, jobber: false, titan: false },
                          { name: "Quote-to-Invoice Conversion", qsg: true, jobber: true, titan: true },
                          { name: "View Tracking & Read Receipts", qsg: true, jobber: false, titan: false },
                          { name: "E-Signatures", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "Payments",
                        features: [
                          { name: "Online Payments", qsg: true, jobber: true, titan: true },
                          { name: "Credit/Debit Cards", qsg: true, jobber: true, titan: true },
                          { name: "PayPal Integration", qsg: true, jobber: false, titan: false },
                          { name: "Record Offline Payments", qsg: true, jobber: true, titan: true },
                          { name: "Recurring Billing", qsg: true, jobber: true, jobberNote: "Higher tiers", titan: true },
                          { name: "Payment Plans / Installments", qsg: true, jobber: false, titan: true, titanNote: "Add-on" },
                          { name: "No Platform Transaction Fee", qsg: true, jobber: false, jobberNote: "0.5-1.5%", titan: false, titanNote: "Varies" },
                        ]
                      },
                      {
                        category: "Client Management",
                        features: [
                          { name: "Client Database", qsg: true, jobber: true, titan: true },
                          { name: "Multiple Properties per Client", qsg: true, jobber: true, titan: true },
                          { name: "Client Messaging Portal", qsg: true, jobber: true, titan: true },
                          { name: "Client Portal Access", qsg: true, jobber: true, titan: true },
                          { name: "Complete Client History", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "Scheduling",
                        features: [
                          { name: "Online Booking Page", qsg: true, jobber: true, titan: true },
                          { name: "Working Hours Setup", qsg: true, jobber: true, titan: true },
                          { name: "Blackout Dates", qsg: true, jobber: true, titan: true },
                          { name: "Service Type Selection", qsg: true, jobber: true, titan: true },
                          { name: "Auto Client Creation", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "Notifications",
                        features: [
                          { name: "Email Notifications", qsg: true, jobber: true, titan: true },
                          { name: "SMS Notifications", qsg: true, jobber: true, titan: true },
                          { name: "Automated Reminders", qsg: true, jobber: true, titan: true },
                          { name: "Quote/Invoice View Alerts", qsg: true, jobber: true, titan: true },
                          { name: "Payment Received Alerts", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "AI & Automation",
                        features: [
                          { name: "AI Quote Builder", qsg: true, jobber: false, titan: false },
                          { name: "AI Line Item Descriptions", qsg: true, jobber: false, titan: false },
                          { name: "AI Website Copy Generator", qsg: true, jobber: false, titan: false },
                          { name: "AI Chatbot Widget", qsg: true, jobber: false, titan: false },
                          { name: "Quote Templates / Template Vault", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "Business Tools",
                        features: [
                          { name: "Custom Branding & Logo", qsg: true, jobber: true, titan: true },
                          { name: "White-Label / Pro Branding", qsg: true, jobber: false, titan: false },
                          { name: "Service Catalog", qsg: true, jobber: true, titan: true },
                          { name: "Project Tracking", qsg: true, jobber: true, titan: true },
                          { name: "Team Members & Permissions", qsg: true, jobber: true, jobberNote: "$29+/user", titan: true, titanNote: "Per user" },
                          { name: "Advanced Reporting", qsg: true, jobber: true, jobberNote: "Higher tiers", titan: true },
                          { name: "Mobile Responsive", qsg: true, jobber: true, titan: true },
                          { name: "In-App Support", qsg: true, jobber: true, titan: true },
                        ]
                      },
                      {
                        category: "Setup & Learning",
                        features: [
                          { name: "Setup Time", qsg: true, qsgNote: "< 10 min", jobber: true, jobberNote: "1-2 hours", titan: true, titanNote: "Days/Weeks" },
                          { name: "No Training Required", qsg: true, jobber: false, titan: false },
                          { name: "Self-Service Onboarding", qsg: true, jobber: true, titan: false, titanNote: "Rep required" },
                        ]
                      }
                    ].map((section, sectionIndex) => (
                      <>
                        <tr key={`header-${sectionIndex}`} className="bg-muted/30">
                          <td colSpan={4} className="p-3 font-bold text-foreground text-sm uppercase tracking-wide border-b border-border/30">{section.category}</td>
                        </tr>
                        {section.features.map((feature, featureIndex) => (
                          <tr key={`${sectionIndex}-${featureIndex}`} className={featureIndex % 2 === 0 ? "bg-background" : "bg-muted/10"}>
                            <td className="p-4 text-sm text-foreground border-b border-border/30 font-medium">{feature.name}</td>
                            <td className="p-4 text-center border-b border-border/30 bg-primary/5">
                              {typeof feature.qsg === 'boolean' ? (
                                feature.qsg ? (
                                  <div className="flex flex-col items-center">
                                    <CheckCircle className="w-5 h-5 text-primary" />
                                    {feature.qsgNote && <span className="text-xs text-primary font-medium mt-1">{feature.qsgNote}</span>}
                                  </div>
                                ) : <span className="text-muted-foreground">—</span>
                              ) : <span className="text-sm font-medium text-primary">{feature.qsg}</span>}
                            </td>
                            <td className="p-4 text-center border-b border-border/30">
                              {typeof feature.jobber === 'boolean' ? (
                                feature.jobber ? (
                                  <div className="flex flex-col items-center">
                                    <CheckCircle className="w-5 h-5 text-muted-foreground" />
                                    {feature.jobberNote && <span className="text-xs text-muted-foreground mt-1">{feature.jobberNote}</span>}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <span className="text-muted-foreground">—</span>
                                    {feature.jobberNote && <span className="text-xs text-muted-foreground mt-1">{feature.jobberNote}</span>}
                                  </div>
                                )
                              ) : <span className="text-sm text-muted-foreground">{feature.jobber}</span>}
                            </td>
                            <td className="p-4 text-center border-b border-border/30">
                              {typeof feature.titan === 'boolean' ? (
                                feature.titan ? (
                                  <div className="flex flex-col items-center">
                                    <CheckCircle className="w-5 h-5 text-muted-foreground" />
                                    {feature.titanNote && <span className="text-xs text-muted-foreground mt-1">{feature.titanNote}</span>}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <span className="text-muted-foreground">—</span>
                                    {feature.titanNote && <span className="text-xs text-muted-foreground mt-1">{feature.titanNote}</span>}
                                  </div>
                                )
                              ) : <span className="text-sm text-muted-foreground">{feature.titan}</span>}
                            </td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Comparison Summary */}
              <div className="mt-12 grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-primary bg-primary/5">
                  <CardHeader className="text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                      <DollarSign className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl text-primary">Best Value</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">Save over <span className="font-bold text-foreground">$600/year</span> compared to Jobber. No per-user fees, no transaction fees, no hidden costs.</p>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader className="text-center">
                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-7 h-7 text-foreground" />
                    </div>
                    <CardTitle className="text-xl">AI-Powered</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">The only platform with <span className="font-bold text-foreground">AI quote building, AI content generation</span>, and an AI chatbot — all included free.</p>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader className="text-center">
                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-7 h-7 text-foreground" />
                    </div>
                    <CardTitle className="text-xl">Everything You Need</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">Professional quoting, invoicing, payments, scheduling, AI tools, and team management—all <span className="font-bold text-foreground">in one $39/mo plan</span>.</p>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center mt-10">
                <p className="text-sm text-muted-foreground mb-6">* Pricing and features based on publicly available information as of January 2026. Competitor pricing may vary by plan, location, and promotions.</p>
              </div>
            </div>
          </div>
        </section>

        <IntervalCTA />


        {/* Security & Trust */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-6 py-2 mb-6">
                <Shield className="w-5 h-5 text-primary" />
                <span className="text-sm font-semibold text-foreground">SECURE & TRUSTED</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">All Payments Processed Safely Through PayPal</h2>
              <p className="text-xl text-muted-foreground">Your clients' payment information is protected by bank-level security</p>
              <div className="mt-8 max-w-sm mx-auto">
                <img src={salesSecurityTrust} alt="Bank-level security and trust" className="rounded-2xl shadow-lg w-full" loading="lazy" />
              </div>
            </div>

            <div className="max-w-4xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, title: "Bank-Level Security", desc: "Industry-leading encryption through PayPal" },
                { icon: DollarSign, title: "Direct to You", desc: "Payments go straight to your PayPal account" },
                { icon: Lock, title: "No Data Stored", desc: "We never store credit card information" },
                { icon: CreditCard, title: "SSL Encrypted", desc: "256-bit SSL encryption for all data" }
              ].map((item, index) => (
                <Card key={index} className="border-border/50 text-center">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-base">{item.title}</CardTitle>
                    <CardDescription>{item.desc}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-16 md:py-24 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold">Here's Everything You're Getting</h2>

              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-xl space-y-4 text-left max-w-xl mx-auto">
                {[
                  { label: "24/7 Online Booking & Scheduling", value: "$60/yr" },
                  { label: "Unlimited Professional Quotes", value: "$96/yr" },
                  { label: "Unlimited Invoices & Billing", value: "$96/yr" },
                  { label: "Online Payment Collection (PayPal)", value: "$120/yr" },
                  { label: "Deposit Collection (% or Fixed Amount)", value: "$48/yr" },
                  { label: "Record Offline Payments (Cash/Check/Transfer)", value: "$36/yr" },
                  { label: "Full Client CRM & Contact Management", value: "$60/yr" },
                  { label: "Client Messaging Portal", value: "$48/yr" },
                  { label: "Project Tracking & Management", value: "$60/yr" },
                  { label: "Invoice-from-Project Workflow", value: "$36/yr" },
                  { label: "Email Notifications (Quotes & Invoices)", value: "$48/yr" },
                  { label: "SMS Notifications & View Alerts", value: "$60/yr" },
                  { label: "Automated Follow-Up Reminders", value: "$48/yr" },
                  { label: "Recurring Billing & Payment Plans", value: "$72/yr" },
                  { label: "Team Members & Permission Controls", value: "$96/yr" },
                  { label: "AI Quote Builder", value: "$120/yr" },
                  { label: "AI Line Item Descriptions", value: "$60/yr" },
                  { label: "AI Website Copy Generator", value: "$60/yr" },
                  { label: "Template Vault (Save & Reuse Quotes)", value: "$48/yr" },
                  { label: "Advanced Reporting & Analytics", value: "$72/yr" },
                  { label: "Pro Branding Suite (White-Label)", value: "$96/yr" },
                  { label: "Professional Business Website", value: "$120/yr" },
                  { label: "Installable Mobile App (PWA)", value: "$60/yr" },
                  { label: "Pre-Loaded Demo Data (26+ Industries)", value: "$36/yr" },
                  { label: "Expense Tracking & Receipt Management", value: "$48/yr" },
                  { label: "Google Review Requests", value: "$36/yr" },
                  { label: "Custom Email Templates", value: "$36/yr" },
                  { label: "Embeddable Booking Widget", value: "$48/yr" },
                  { label: "Custom Domain Support", value: "$60/yr" },
                  { label: "AI Chatbot Widget", value: "$60/yr" },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-border/30 last:border-0">
                    <span className="text-sm text-foreground flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {item.label}
                    </span>
                    <span className="text-sm text-muted-foreground line-through ml-4 whitespace-nowrap">{item.value}</span>
                  </div>
                ))}

                <div className="pt-4 border-t-2 border-primary/30">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-foreground">Total Retail Value</span>
                    <span className="font-bold text-foreground line-through text-xl">$2,004/yr</span>
                  </div>
                </div>
                <div className="pt-6 space-y-3 text-center">
                  <div className="text-sm text-muted-foreground">Your price:</div>
                  
                  {/* Monthly / Yearly Toggle */}
                  <div className="flex items-center justify-center gap-3">
                    <span className={`text-sm font-medium transition-colors ${!isYearly ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
                    <button
                      onClick={() => setIsYearly(!isYearly)}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${isYearly ? 'bg-primary' : 'bg-muted'}`}
                      aria-label="Toggle yearly pricing"
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-background shadow-md transition-transform ${isYearly ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-sm font-medium transition-colors ${isYearly ? 'text-foreground' : 'text-muted-foreground'}`}>
                      Yearly
                      <Badge variant="secondary" className="ml-2 text-xs">Save 26%</Badge>
                    </span>
                  </div>

                  <div className="text-7xl font-black text-primary">
                    {isYearly ? '$29' : '$39'}<span className="text-3xl font-bold">/mo</span>
                  </div>
                  {isYearly && (
                    <div className="text-sm text-muted-foreground">Billed annually at <strong className="text-foreground">$348/year</strong></div>
                  )}
                  <div className="text-xl text-muted-foreground">One plan. All features. AI included. <strong className="text-foreground">No hidden fees.</strong></div>
                </div>

                <div className="space-y-4 pt-4 text-center">
                  <Button
                    size="lg"
                    className="w-full max-w-md text-lg py-6 font-bold shadow-lg hover:scale-105 transition-all bg-success hover:bg-success/90"
                    onClick={() => navigate("/auth")}
                  >
                    👉 Start Your Free 14-Day Trial
                  </Button>
                  <p className="text-xs text-muted-foreground">No credit card required • Full access for 14 days</p>
                </div>

                {/* Trial Policy */}
                <div className="mt-8 pt-8 border-t border-border/50 text-center space-y-4">
                  <Shield className="h-12 w-12 text-primary mx-auto" />
                  <h3 className="text-2xl font-bold text-foreground">Try Before You Buy — 14-Day Free Trial</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Get full, unrestricted access to every feature for 14 days — <strong className="text-foreground">no credit card required</strong>. 
                    Send quotes, book clients, collect payments, use the AI tools — the entire platform is yours to explore.
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    If you love it, subscribe to keep going. <strong className="text-foreground">Because you get a full free trial to evaluate the platform, 
                    all subscriptions are final — no refunds after subscribing.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Are You Ready To Take Action? */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10">
              <div className="w-full md:w-3/5 space-y-6 text-center md:text-left">
                <h2 className="text-3xl md:text-5xl font-bold text-foreground">Are You Ready to Take Action?</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  You've seen what QuoteSendGo can do. You know the value. Right now, you have two choices:
                </p>
                <div className="grid sm:grid-cols-2 gap-6 text-left">
                  <Card className="border-border/50">
                    <CardContent className="pt-6 space-y-3">
                      <XCircle className="h-8 w-8 text-destructive" />
                      <h3 className="font-bold text-foreground">Option A: Do Nothing</h3>
                      <p className="text-sm text-muted-foreground">Keep using spreadsheets. Keep chasing payments. Keep losing quotes in your email. Keep spending hours on things that should take minutes.</p>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-primary bg-primary/5">
                    <CardContent className="pt-6 space-y-3">
                      <CheckCircle className="h-8 w-8 text-primary" />
                      <h3 className="font-bold text-primary">Option B: Get Started Now</h3>
                      <p className="text-sm text-muted-foreground">Start your free 14-day trial — no credit card needed. Let AI build your quotes, send professional invoices, and get paid faster — today.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              <div className="w-full md:w-2/5 flex-shrink-0">
                <img src={salesSecurityTrust} alt="Professional ready to grow their business" className="rounded-2xl shadow-lg w-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* Second CTA Card */}
        <section className="py-16 md:py-24 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-lg mx-auto">
              <Card className="border-2 border-success bg-success/5 shadow-xl">
                <CardContent className="pt-8 pb-8 space-y-5 text-center">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-primary/20">
                    🚀 14-Day Free Trial — No Credit Card Required
                  </Badge>
                  <div>
                    <div className="text-7xl font-black text-success">$39/mo</div>
                    <div className="text-base text-muted-foreground mt-1">One plan. All features. AI included. <strong className="text-foreground">No hidden fees.</strong></div>
                  </div>
                  <ul className="text-sm text-left space-y-1.5 text-foreground max-w-xs mx-auto">
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success shrink-0" /> AI-powered quote building</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success shrink-0" /> Unlimited quotes & invoices</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success shrink-0" /> Online booking & payments</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success shrink-0" /> Full CRM, branding & team members</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success shrink-0" /> SMS, reminders & recurring billing</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success shrink-0" /> 14-day free trial, no credit card</li>
                  </ul>
                  <Button
                    size="lg"
                    className="w-full text-lg py-6 font-bold shadow-lg hover:scale-105 transition-all bg-success hover:bg-success/90"
                    onClick={() => navigate("/auth")}
                  >
                    👉 Start Your Free Trial
                  </Button>
                  <p className="text-xs text-muted-foreground">No credit card required • Cancel anytime during trial</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center space-y-4 mb-10">
                <img src={salesFaq} alt="Frequently asked questions" className="rounded-2xl shadow-lg w-full max-w-xs mx-auto mb-4" loading="lazy" />
                <h2 className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</h2>
                <p className="text-lg text-muted-foreground">Got questions? We've got answers.</p>
              </div>
              <Accordion type="single" collapsible className="space-y-2">
                {[
                  { q: "What's included in the $39/month plan?", a: "Everything — unlimited quotes, invoices, clients, projects, online booking, PayPal payment collection, deposit collection, client CRM, messaging portal, custom branding, SMS notifications, automated reminders, business website, view tracking, e-signatures, AI Quote Builder, AI line descriptions, AI website copy, template vault, advanced reporting, pro branding suite, team members, recurring billing, payment plans, and more. No per-user fees." },
                  { q: "How does the AI Quote Builder work?", a: "Simply describe your project in plain English — for example, 'Full kitchen renovation with custom cabinets, granite countertops, and new flooring.' The AI generates a complete, itemized quote with individual line items, descriptions, and estimated pricing. You can review, edit, and send it in under a minute." },
                  { q: "How do I collect payments from clients?", a: "Connect your PayPal account (just enter your PayPal email). When clients pay, money goes directly to your PayPal, then transfers to your bank according to your PayPal settings. Clients can pay with credit cards, debit cards, or PayPal — no PayPal account required." },
                  { q: "What if I'm not tech-savvy? Is this hard to set up?", a: "Not at all. If you can send an email, you can use QuoteSendGo. Most users create their first quote within 10 minutes of signing up. The AI does most of the work for you. We also have video tutorials and a support team to help." },
                  { q: "Will this work for my type of service business?", a: "QuoteSendGo works for any service business that sends quotes and invoices — contractors, landscapers, painters, cleaners, plumbers, electricians, handymen, HVAC, pressure washers, pool services, and more." },
                  { q: "Can I invite my team members?", a: "Yes! Add team members to your account with role-based permissions. Control what each person can see and do — from full admin access to view-only. No per-user fees." },
                  { q: "Can I put my own logo and branding on quotes?", a: "Absolutely. Upload your logo, set brand colors, and every quote, invoice, and client-facing page will carry your professional branding. The Pro Branding Suite also lets you white-label the entire platform." },
                  { q: "Is there a free trial?", a: "Yes! You get a full 14-day free trial with complete access to every feature — no credit card required. Use the AI tools, send quotes, book clients, and explore the entire platform. If you love it, subscribe to continue. Because the trial gives you full access to evaluate the platform before paying, all subscriptions are final with no refunds." },
                  { q: "Do my clients need to create an account?", a: "No. Clients receive a secure link to view and approve quotes, pay invoices, and book appointments. No app downloads, no account creation required." },
                  { q: "Can I use QuoteSendGo in my country?", a: "Absolutely! QuoteSendGo works worldwide with no geographic restrictions. The platform interface is currently in English, with additional languages planned for a future update." },
                  { q: "Are there limits on clients, quotes, or invoices?", a: "No limits! Create unlimited clients, quotes, invoices, and projects. The $39/month subscription gives you full access to everything." },
                  { q: "What if I need help?", a: "Submit support tickets directly from your dashboard. We also offer email support at support@quotesendgo.com. Most questions are answered within 24 hours on business days." },
                ].map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-lg px-4 bg-card">
                    <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline">{faq.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center space-y-6">
              <p className="text-lg text-muted-foreground">Still on the fence? Start your <strong className="text-foreground">14-day free trial</strong> — no credit card required. Experience every feature before you commit.</p>
              <Button
                size="lg"
                className="text-lg px-8 py-6 font-bold shadow-lg hover:scale-105 transition-all bg-success hover:bg-success/90"
                onClick={() => navigate("/auth")}
              >
                👉 Start Your Free 14-Day Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-sm text-muted-foreground">No credit card required. All features included. AI-powered.</p>
            </div>
          </div>
        </section>

        {/* Affiliate Program Section */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border-y border-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10 space-y-3">
                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                  <Handshake className="w-3.5 h-3.5 mr-1.5" />
                  Affiliate Program
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  Earn 35% Recurring Commission
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Love QuoteSendGo? Get paid to share it. Join our affiliate program and earn recurring commissions for every customer you refer.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-10">
                <Card className="border-2 border-primary/20 bg-card hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 text-center space-y-3">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Percent className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-bold text-foreground">35% Commission</h3>
                    <p className="text-sm text-muted-foreground">
                      Earn 35% on every payment your referrals make — for the lifetime of their subscription.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-primary/20 bg-card hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 text-center space-y-3">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Cookie className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-bold text-foreground">60-Day Cookie</h3>
                    <p className="text-sm text-muted-foreground">
                      Generous tracking window means you get credit even if your referral signs up later.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-primary/20 bg-card hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 text-center space-y-3">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-bold text-foreground">Fast Payouts</h3>
                    <p className="text-sm text-muted-foreground">
                      Real-time dashboard, transparent reporting, and reliable monthly payouts via PayPal.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center space-y-4">
                <Button
                  size="lg"
                  className="text-lg px-8 py-6 font-bold shadow-lg hover:scale-105 transition-all"
                  onClick={() => navigate("/affiliates")}
                >
                  <Handshake className="mr-2 w-5 h-5" />
                  Join the Affiliate Program
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-sm text-muted-foreground">
                  Free to join · No minimums to apply · Get your link in minutes
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-muted/30 mt-auto">
        <EvergreenPromise />
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <img src={logo} alt="QuoteSendGo Logo" className="h-10 w-auto" width="500" height="120" />
            </div>
            <p className="text-muted-foreground max-w-md mx-auto">
              The AI-powered quote and invoice management solution for service businesses.
            </p>
            <div className="flex justify-center gap-6 text-sm text-muted-foreground flex-wrap">
              <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
              <Link to="/affiliates" className="hover:text-foreground transition-colors">Affiliates</Link>
              <Link to="/support-docs" className="hover:text-foreground transition-colors">Support Docs</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
            <p className="text-sm text-muted-foreground pt-4">
              © {new Date().getFullYear()} Quote Send Go. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
